/**
 * usePermissions - RBAC permission hook
 *
 * Returns permission flags based on the current user's role.
 * Uses the ROLE_PERMISSIONS mapping from shared-types to determine
 * what actions the user can perform.
 *
 * Implementation: HOME-12 RBAC requirement
 *
 * Usage:
 * ```tsx
 * const permissions = usePermissions();
 *
 * if (!permissions.canCreateBoard) {
 *   return <p>You don't have permission to create boards</p>;
 * }
 *
 * return <button disabled={!permissions.canUploadFiles}>Upload</button>;
 * ```
 *
 * Note: This provides frontend UI gating only.
 * Backend API routes must enforce permissions separately.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ROLE_PERMISSIONS,
  type Permission,
  type UserRole,
  type AuthUser,
} from '@deep-agents/shared-types';

/**
 * Fetch current user from /api/auth/me
 * This is the same endpoint used by useCurrentUser, but we need
 * the full AuthUser object (with role) not just PresenceUser.
 */
async function fetchCurrentUser(): Promise<AuthUser | null> {
  const response = await fetch('/api/auth/me', {
    credentials: 'include',
  });

  if (!response.ok) {
    return null;
  }

  const data = await response.json();
  // API returns { data: { user: { id, email, role, ... } } }
  return data.data?.user || null;
}

/**
 * Hook return type includes permissions + metadata
 */
interface UsePermissionsResult extends Permission {
  /** Current user's role (null if not authenticated) */
  role: UserRole | null;
  /** Whether user data is still loading */
  isLoading: boolean;
}

/**
 * usePermissions hook
 *
 * Returns permission flags based on user's role from auth context.
 * If user is not authenticated or loading, defaults to viewer permissions
 * (most restrictive) to fail-closed.
 */
export function usePermissions(): UsePermissionsResult {
  // Fetch current user (cached by React Query)
  const { data: user, isLoading } = useQuery<AuthUser | null>({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
    staleTime: 5 * 60 * 1000, // 5 minutes (same as useCurrentUser)
    retry: false,
  });

  // Compute permissions based on role
  const result = useMemo((): UsePermissionsResult => {
    // Default to viewer (most restrictive) if no user or still loading
    // This is a fail-closed security pattern
    const role: UserRole = user?.role || 'viewer';
    const permissions = ROLE_PERMISSIONS[role];

    return {
      ...permissions,
      role: user?.role || null,
      isLoading,
    };
  }, [user, isLoading]);

  return result;
}
